import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { CATEGORY_LABEL_KEYS } from "@/data/journalArticles";
import { pickLocale } from "@/lib/localize";

/**
 * Card used on the Journal index. Displays cover, category, title, excerpt,
 * date, and reading time. Pulls localized fields from the article record.
 *
 * Props:
 *   article — entry from /data/journalArticles.js
 *   variant — "featured" (large hero card) | "default" (compact grid card)
 */
export default function ArticleCard({ article, variant = "default" }) {
  const { t, lang } = useT();

  const title = pickLocale(article.title, lang);
  const excerpt = pickLocale(article.excerpt, lang);
  const author = pickLocale(article.author, lang);
  const categoryLabel = t(CATEGORY_LABEL_KEYS[article.category] || "journal.cat.reflections");

  const dateLocale = { nl: "nl-NL", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES" }[lang] || "en-US";
  const dateLong = new Date(article.publishedAt).toLocaleDateString(dateLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const href = `/stoic-journal/${article.slug}`;

  if (variant === "featured") {
    return (
      <Link
        to={href}
        data-testid={`journal-card-featured-${article.slug}`}
        className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch border border-white/8 hover:border-gold/35 transition-all duration-500 bg-ink-elevated/30"
      >
        <div className="lg:col-span-7 relative overflow-hidden aspect-[16/10] lg:aspect-auto lg:min-h-[460px]">
          <img
            src={article.cover}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background:
                "linear-gradient(120deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.55) 100%)",
            }}
          />
        </div>
        <div className="lg:col-span-5 flex flex-col justify-center p-6 lg:p-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-gold mb-5 flex items-center gap-3">
            <span className="h-px w-6 bg-gold/60" />
            {t("journal.featuredLabel")} · {categoryLabel}
          </p>
          <h2 className="font-serif text-bone text-3xl md:text-4xl tracking-tight leading-[1.1] group-hover:text-gold transition-colors duration-500">
            {title}
          </h2>
          <p className="mt-5 text-[11px] tracking-[0.22em] uppercase text-fog/85">
            {author} · {dateLong}
          </p>
          <p className="mt-6 font-reading text-bone/80 text-base md:text-lg leading-[1.85] line-clamp-4">
            {excerpt}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold">
            <Clock size={12} strokeWidth={1.5} />
            {article.readingTimeMin} {t("journal.readingTime")}
            <span className="mx-3 text-bone/30">·</span>
            <span className="inline-flex items-center gap-2 group-hover:text-bone transition-colors">
              {t("journal.featuredReadCta")}
              <ArrowRight size={12} strokeWidth={1.5} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      data-testid={`journal-card-${article.slug}`}
      className="group flex flex-col border border-white/8 hover:border-gold/35 transition-all duration-500 bg-ink-elevated/30"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.cover}
          alt={title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background:
              "linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>
      <div className="flex flex-col p-6 md:p-8 flex-1">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-4">
          {categoryLabel}
        </p>
        <h3 className="font-serif text-bone text-xl md:text-2xl tracking-tight leading-[1.15] group-hover:text-gold transition-colors duration-500">
          {title}
        </h3>
        <p className="mt-3 text-[11px] tracking-[0.2em] uppercase text-fog/80" data-testid="card-author-date">
          {author} <span className="text-bone/30">·</span> {dateLong}
        </p>
        <p className="mt-4 font-reading text-bone/75 text-base leading-[1.8] line-clamp-3">
          {excerpt}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold/85 group-hover:text-gold transition-colors">
          <Clock size={12} strokeWidth={1.5} />
          {article.readingTimeMin} {t("journal.readingTime")}
          <ArrowRight size={12} strokeWidth={1.5} className="ml-auto" />
        </div>
      </div>
    </Link>
  );
}
